import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CostSummary from './CostSummary';
import DiscountCodeField from './DiscountCodeField';
import { CloseIcon, TubeIcon } from '../../icons';
import { computeVenueDepositQuote } from '../../../utils/venueDeposits';

import { ReactComponent as MagicStarIcon } from '../../icons/magic-star-icon.svg';

const VenueSidebar = ({
  metroDistance,
  pricingDetails,
  venue,
  hidePrice = false,
  submitSlotRef = null,
  discountCode = '',
  discountCodeValidation = null,
  discountError = '',
  isValidatingDiscount = false,
  onDiscountCodeChange = () => {},
  onValidateDiscountCode = () => {},
  onClearDiscountCode = () => {},
  stripePaymentsUiEnabled = false,
}) => {
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    if (!breakdownOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setBreakdownOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [breakdownOpen]);

  const depositQuote = useMemo(
    () =>
      !venue || hidePrice
        ? null
        : computeVenueDepositQuote(
            venue,
            {
              venueSubtotal: pricingDetails?.bookingSubtotal,
              estimatedTotal: pricingDetails?.estimatedTotal,
            },
            stripePaymentsUiEnabled
          ),
    [venue, pricingDetails, hidePrice, stripePaymentsUiEnabled]
  );

  if (!venue) {
    return null;
  }

  const {
    hourlyRate,
    duration,
    baseFee: baseFeeProp,
    cleaningFee,
    processingFee,
    platformFee = 0,
    estimatedTotal,
    bookingSubtotal,
    discountAmount = 0,
    discountCode: appliedDiscountCode = null,
    addOnsTotal = 0,
    selectedAddOns = [],
    isPerPerson = false,
    tableCount = null,
    perPersonRate = null,
    usingFlatPricing = false,
    matchingFlatRule = null,
    isDiscounted = false,
  } = pricingDetails;

  // Get primary image from API response
  const getPrimaryImage = () => {
    // First try primaryImageUrl if available
    if (venue.primaryImageUrl) {
      return venue.primaryImageUrl;
    }
    // Then try to find primary image in images array
    if (venue.images && Array.isArray(venue.images)) {
      const primaryImage = venue.images.find(img => 
        img.is_primary === true || img.image_type === 'primary'
      );
      if (primaryImage && primaryImage.image_url) {
        return primaryImage.image_url;
      }
      // Fallback to first image if no primary found
      if (venue.images.length > 0 && venue.images[0].image_url) {
        return venue.images[0].image_url;
      }
    }
    // Fallback to legacy image property
    if (venue.image) {
      return venue.image;
    }
    // Final fallback placeholder
    return 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=2338&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
  };

  const venueImage = getPrimaryImage();
  const ratingDisplay = venue.rating || '0';
  // Review count is not in API response, so we'll set it to 0 or make it optional
  const reviewCountDisplay = venue.reviewCount || venue.review_count || 0;
  // Use address or area field from API
  const venueAddress = venue.address || venue.area || '';

  return (
    <aside className="venue-details-column">
      <div className="venue-card-booking">
        <div className="venue-card-booking-body">
        <div className="venue-preview-card">
        <div className="venue-image-booking-page">
          <img
            src={venueImage}
            alt={venue.name}
            onError={(event) => {
              // Fallback to placeholder if image fails to load
              event.target.src = 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=2338&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
            }}
          />
        </div>
        <div className="venue-info-booking">
          <div className="venue-header-row">
            <h3 className="venue-name-booking">{venue.name}</h3>
            <div className="venue-rating-booking">
              <MagicStarIcon className="rating-icon" aria-hidden="true" />
              <span className="rating-text">{ratingDisplay} ({reviewCountDisplay})</span>
            </div>
          </div>
          <div className="venue-details-section">
            <div className="venue-location-booking">
              <img
                className="location-icon-booking"
                src="/manual-booking/icons/pending-location.png"
                alt=""
                aria-hidden="true"
                width="20"
                height="20"
              />
              <span>{venueAddress}</span>
            </div>
            <div className="venue-proximity-booking">
              <TubeIcon className="subway-icon-booking" width="20" height="20" aria-hidden="true" />
              <span>{metroDistance}</span>
            </div>
          </div>
        </div>
        </div>

        {hidePrice ? (
          <div className="venue-cost-breakdown" style={{ padding: '12px 0', color: '#585858', fontSize: '14px' }}>
            Contact venue for pricing
          </div>
        ) : (
          <>
          {breakdownOpen ? (
            <button
              type="button"
              className="booking-breakdown-backdrop"
              aria-label="Close"
              onClick={() => setBreakdownOpen(false)}
            />
          ) : null}
          <div
            className={`booking-breakdown-sheet${breakdownOpen ? ' is-open' : ''}`}
            role={breakdownOpen ? 'dialog' : undefined}
            aria-modal={breakdownOpen ? 'true' : undefined}
            aria-label={breakdownOpen ? 'Price breakdown' : undefined}
          >
            <div className="booking-breakdown-sheet-bar">
              <h2 className="booking-breakdown-sheet-title">Price breakdown</h2>
              <button
                type="button"
                className="booking-breakdown-sheet-close"
                aria-label="Close"
                onClick={() => setBreakdownOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="booking-breakdown-sheet-body">
              <CostSummary
                className="cost-summary--venue-sidebar"
                title=""
                discountSlot={!hidePrice ? (
                  <DiscountCodeField
                    discountCode={discountCode}
                    discountCodeValidation={discountCodeValidation}
                    discountError={discountError}
                    isValidatingDiscount={isValidatingDiscount}
                    onDiscountCodeChange={onDiscountCodeChange}
                    onValidateDiscountCode={onValidateDiscountCode}
                    onClearDiscountCode={onClearDiscountCode}
                  />
                ) : null}
                hourlyRate={hourlyRate}
                duration={duration}
                baseFee={baseFeeProp}
                cleaningFee={cleaningFee}
                processingFee={processingFee}
                platformFee={platformFee}
                venueSubtotal={bookingSubtotal}
                estimatedTotal={estimatedTotal}
                isDiscounted={isDiscounted}
                showBreakdown
                discountAmount={discountAmount}
                discountCode={appliedDiscountCode}
                addOnsTotal={addOnsTotal}
                addOns={selectedAddOns}
                isPerPerson={isPerPerson}
                tableCount={tableCount}
                perPersonRate={perPersonRate}
                usingFlatPricing={usingFlatPricing}
                matchingFlatRule={matchingFlatRule}
                totalLabel="Estimated total"
                depositQuote={depositQuote}
              />
            </div>
          </div>
          </>
        )}
        <div className="booking-mobile-checkout">
          {!hidePrice ? (
            <>
              <div className="booking-mobile-total">
                <span>Estimated total</span>
                <div className="booking-mobile-total-price">
                  <span>£{Number(estimatedTotal || 0).toFixed(2)}</span>
                  <button
                    type="button"
                    className="booking-view-breakdown"
                    onClick={() => setBreakdownOpen(true)}
                  >
                    View breakdown
                  </button>
                </div>
              </div>
            </>
          ) : null}
          {submitSlotRef ? <div ref={submitSlotRef} className="venue-sidebar-submit" /> : null}
        </div>
        </div>
      </div>
    </aside>
  );
};

VenueSidebar.propTypes = {
  metroDistance: PropTypes.string.isRequired,
  pricingDetails: PropTypes.shape({
    hourlyRate: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    baseFee: PropTypes.number,
    cleaningFee: PropTypes.number.isRequired,
    processingFee: PropTypes.number.isRequired,
    platformFee: PropTypes.number,
    estimatedTotal: PropTypes.number.isRequired,
    bookingSubtotal: PropTypes.number,
    isDiscounted: PropTypes.bool,
    discountAmount: PropTypes.number,
    discountCode: PropTypes.string,
    addOnsTotal: PropTypes.number,
    selectedAddOns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      quantity: PropTypes.number
    })),
    isPerPerson: PropTypes.bool,
    tableCount: PropTypes.number,
    perPersonRate: PropTypes.number,
    usingFlatPricing: PropTypes.bool,
    matchingFlatRule: PropTypes.shape({
      min_hours: PropTypes.number,
    }),
  }).isRequired,
  venue: PropTypes.shape({
    address: PropTypes.string,
    area: PropTypes.string,
    image: PropTypes.string,
    primaryImageUrl: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({
      image_url: PropTypes.string,
      image_type: PropTypes.string,
      is_primary: PropTypes.bool
    })),
    name: PropTypes.string.isRequired,
    rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reviewCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    review_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  submitSlotRef: PropTypes.func,
  discountCode: PropTypes.string,
  discountCodeValidation: PropTypes.shape({
    is_valid: PropTypes.bool,
  }),
  discountError: PropTypes.string,
  isValidatingDiscount: PropTypes.bool,
  onDiscountCodeChange: PropTypes.func,
  onValidateDiscountCode: PropTypes.func,
  onClearDiscountCode: PropTypes.func,
  stripePaymentsUiEnabled: PropTypes.bool,
};

VenueSidebar.defaultProps = {
  venue: null
};

export default VenueSidebar;




